// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Dashboard
// KPI cards, debris breakdown, zone overview, recent activity
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SimpleGrid, Paper, Text, Title, Group, Badge, Stack, Table, Loader, Center, Button,
} from '../../components/gab/GabComponents';
import {
  IconMap2, IconBuildingFactory2, IconTruck, IconTicket,
  IconBuildingWarehouse, IconUsers,
} from '@tabler/icons-react';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { fmtDate } from '../../lib/date-format';

// ── Status badge colors ──────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', pending: '#C68700', completed: 'var(--color-primary-700)', closed: '#939598',
  in_transit: '#F86A0B', delivered: 'var(--color-success)', disputed: 'var(--color-error)', voided: '#939598',
  certified: 'var(--color-success)', decertified: 'var(--color-error)',
  open: 'var(--color-success)', full: '#F86A0B',
  standby: '#C68700', out_of_service: 'var(--color-error)',
  suspended: 'var(--color-error)',
};

export default function DebrisDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    zones, contractors, crews, trucks, disposalSites,
    loadingZones, loadingContractors, loadingCrews, loadingTrucks, loadingSites,
    loadZones, loadContractors, loadCrews, loadTrucks, loadDisposalSites,
  } = useDebrisStore();

  const {
    loadTickets, loadingTickets, loadLoadTickets,
  } = useDebrisOpsStore();

  useEffect(() => {
    loadZones();
    loadContractors();
    loadCrews();
    loadTrucks();
    loadDisposalSites();
    loadLoadTickets();
  }, [loadZones, loadContractors, loadCrews, loadTrucks, loadDisposalSites, loadLoadTickets]);

  // Filter to active incident
  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];
  const iCrews = incidentId ? crews : [];
  const iTrucks = incidentId ? trucks : [];
  const iSites = incidentId ? disposalSites : [];
  const iTickets = incidentId ? loadTickets : [];

  // ── Computed stats ─────────────────────────────────────────
  // Compute CY for a ticket — fallback to loadPercentage × truck capacity if calculatedCubicYards is blank
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [iTrucks]);

  const totalCY = useMemo(() =>
    iTickets.reduce((sum, t) => sum + getTicketCY(t), 0),
    [iTickets, getTicketCY],
  );

  const cyByType = useMemo(() => {
    const map: Record<string, number> = {};
    iTickets.forEach(t => {
      if (t.debrisType) {
        map[t.debrisType] = (map[t.debrisType] || 0) + getTicketCY(t);
      }
    });
    return map;
  }, [iTickets, getTicketCY]);

  const activeCrews = iCrews.filter(c => c.status === 'active').length;
  const certifiedTrucks = iTrucks.filter(t => t.status === 'certified').length;
  const activeZones = iZones.filter(z => z.status === 'active').length;
  const openSites = iSites.filter(s => s.status === 'open').length;

  // Sort state for Zone Overview table
  const [zoneSortField, setZoneSortField] = useState('');
  const [zoneSortDir, setZoneSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleZoneSort = (field: string) => {
    if (zoneSortField === field) setZoneSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setZoneSortField(field); setZoneSortDir('asc'); }
  };

  // Sort state for Recent Load Tickets table
  const [ticketSortField, setTicketSortField] = useState('');
  const [ticketSortDir, setTicketSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleTicketSort = (field: string) => {
    if (ticketSortField === field) setTicketSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTicketSortField(field); setTicketSortDir('asc'); }
  };

  const isLoading = loadingZones || loadingContractors || loadingCrews || loadingTrucks || loadingSites || loadingTickets;

  const sortedZones = [...iZones].sort((a, b) => {
    if (!zoneSortField) return 0;
    const va = (a as any)[zoneSortField] ?? '';
    const vb = (b as any)[zoneSortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return zoneSortDir === 'asc' ? n : -n;
  });

  const recentTickets = iTickets.slice(-10).reverse();
  const sortedTickets = [...recentTickets].sort((a, b) => {
    if (!ticketSortField) return 0;
    const va = (a as any)[ticketSortField] ?? '';
    const vb = (b as any)[ticketSortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return ticketSortDir === 'asc' ? n : -n;
  });

  if (isLoading && iTickets.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={3} c="black">Debris Management</Title>
          <Text size="sm" c="dimmed">
            {activeIncident?.name ? `${activeIncident.name} — ` : ''}{iTickets.length} load tickets, {iZones.length} zones
          </Text>
        </div>
      </Group>

      {/* KPI Cards */}
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} style={{ alignItems: 'stretch' }}>
        <KPICard icon={IconTicket} label="Load Tickets" value={iTickets.length}
          sub={`${totalCY.toLocaleString()} CY total`} color="#d97706" onClick={() => navigate('/debris/tickets')} />
        <KPICard icon={IconMap2} label="Zones" value={iZones.length}
          sub={`${activeZones} active`} color="#16a34a" onClick={() => navigate('/debris/zones')} />
        <KPICard icon={IconBuildingFactory2} label="Contractors" value={iContractors.length}
          sub={`${iContractors.filter(c => c.status === 'active').length} active`} color="#0284c7" onClick={() => navigate('/debris/contractors')} />
        <KPICard icon={IconUsers} label="Crews" value={iCrews.length}
          sub={`${activeCrews} active`} color="#7c3aed" onClick={() => navigate('/debris/crews')} />
        <KPICard icon={IconTruck} label="Trucks" value={iTrucks.length}
          sub={`${certifiedTrucks} certified`} color="#B12525" onClick={() => navigate('/debris/trucks')} />
        <KPICard icon={IconBuildingWarehouse} label="Disposal Sites" value={iSites.length}
          sub={`${openSites} open`} color="#92400e" onClick={() => navigate('/debris/sites')} />
      </SimpleGrid>

      {/* Middle Row: Debris Breakdown + Zone Status */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Debris by Type — Treemap */}
        <Paper p="md" radius="md" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Cubic Yards by Debris Type</Text>
            <Text size="sm" fw={600} c="#d97706">{totalCY.toLocaleString()} CY total</Text>
          </Group>
          {Object.keys(cyByType).length === 0 ? (
            <Text c="dimmed" size="sm">No load tickets recorded yet</Text>
          ) : (
            <div style={{ display: 'flex', flex: 1, minHeight: 160, borderRadius: 4, overflow: 'hidden', width: '100%' }}>
              {Object.entries(cyByType)
                .sort(([, a], [, b]) => b - a)
                .map(([type, cy]) => {
                  const pct = totalCY > 0 ? (cy / totalCY) * 100 : 0;
                  const color = DEBRIS_TYPE_COLORS[type as DebrisType] ?? '#6b7280';
                  const label = DEBRIS_TYPE_LABELS[type as DebrisType] ?? type;
                  return (
                    <div
                      key={type}
                      title={`${label}: ${cy.toLocaleString()} CY (${pct.toFixed(1)}%)`}
                      style={{
                        width: `${pct}%`,
                        flexShrink: 0,
                        overflow: 'hidden',
                        height: '100%',
                        background: color,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px 2px',
                        cursor: 'default',
                        transition: 'opacity 150ms',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    >
                      {pct > 12 && <Text size="xs" fw={700} c="white" ta="center" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{label}</Text>}
                      {pct > 8 && <Text size="xs" c="white" ta="center" style={{ opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.5)', fontFamily: 'DM Mono, monospace' }}>{cy.toLocaleString()}</Text>}
                    </div>
                  );
                })}
            </div>
          )}
        </Paper>

        {/* Zone Status Table */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Zone Overview</Text>
            {iZones.length > 0 && (
              <Button size="compact-xs" variant="subtle" onClick={() => navigate('/debris/zones')}>
                View All ({iZones.length})
              </Button>
            )}
          </Group>
          {iZones.length === 0 ? (
            <Text c="dimmed" size="sm">No zones created yet</Text>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  {[
                    { label: 'Zone', field: 'zoneName', ta: undefined },
                    { label: 'Status', field: 'status', ta: undefined },
                    { label: 'Est. CY', field: 'estimatedCubicYards', ta: 'right' as const },
                    { label: 'Actual CY', field: 'actualCubicYards', ta: 'right' as const },
                  ].map(col => (
                    <Table.Th
                      key={col.label}
                      ta={col.ta}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        color: zoneSortField === col.field ? '#4B3FFF' : '#7B7D7F',
                      }}
                      onClick={() => toggleZoneSort(col.field)}>
                      {col.label}
                      {zoneSortField === col.field
                        ? (zoneSortDir === 'desc' ? ' ↓' : ' ↑')
                        : <span style={{ opacity: 0.25 }}> ↑</span>}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedZones.map(z => (
                  <Table.Tr key={z.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/debris/zones')}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{z.zoneName || z.zoneCode}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLORS[z.status] ?? '#939598'}>
                        {z.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{(Number(z.estimatedCY) || 0).toLocaleString()}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{(Number(z.actualCY) || 0).toLocaleString()}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          )}
        </Paper>
      </SimpleGrid>

      {/* Recent Tickets */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={700}>Recent Load Tickets</Text>
          {iTickets.length > 10 && (
            <Button size="compact-xs" variant="subtle" onClick={() => navigate('/debris/tickets')}>
              View All ({iTickets.length})
            </Button>
          )}
        </Group>
        {iTickets.length === 0 ? (
          <Text c="dimmed" size="sm">No load tickets recorded yet</Text>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                {[
                  { label: 'Ticket #', field: 'ticketNumber', ta: undefined },
                  { label: 'Truck', field: 'truckNumber', ta: undefined },
                  { label: 'Type', field: 'debrisType', ta: undefined },
                  { label: 'CY', field: 'calculatedCubicYards', ta: 'right' as const },
                  { label: 'Status', field: 'status', ta: undefined },
                  { label: 'Pickup', field: 'pickupTime', ta: undefined },
                ].map(col => (
                  <Table.Th
                    key={col.label}
                    ta={col.ta}
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      color: ticketSortField === col.field ? '#4B3FFF' : '#7B7D7F',
                    }}
                    onClick={() => toggleTicketSort(col.field)}>
                    {col.label}
                    {ticketSortField === col.field
                      ? (ticketSortDir === 'desc' ? ' ↓' : ' ↑')
                      : <span style={{ opacity: 0.25 }}> ↑</span>}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedTickets.map(t => (
                <Table.Tr key={t.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{t.ticketNumber || `#${t.id}`}</Text>
                  </Table.Td>
                  <Table.Td><Text size="sm">{t.truckNumber}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={DEBRIS_TYPE_COLORS[t.debrisType as DebrisType] ?? '#939598'}>
                      {DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{getTicketCY(t).toLocaleString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={STATUS_COLORS[t.status] ?? '#939598'}>
                      {t.status ? t.status.replace(/_/g, ' ') : 'in transit'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{fmtDate(t.pickupTime)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

// ── KPI Card Component ──────────────────────────────────────

function KPICard({ label, value, sub, color, onClick }: {
  icon?: React.ComponentType<{ size?: number; stroke?: number }>;
  label: string;
  value: number;
  sub: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <Paper p="sm" radius="md" withBorder ta="center"
      style={{ cursor: 'pointer', borderTop: `3px solid ${color}`, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClick}>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text fz={28} fw={700} style={{ color }}>{value}</Text>
      <Text size="xs" c="dimmed">{sub}</Text>
    </Paper>
  );
}
